import { AreaChart } from "@tremor/react";
import { useState, useEffect, useMemo } from 'react';

import { formatDateLong, formatDate, formatDate3 } from '../../utils/formatDates.js';

const customTooltipUsers = ({ payload, active, label }) => {
    if (!active || !payload) return null;
    return (
        <div className="w-56 rounded-tremor-default text-tremor-default bg-tremor-background p-2 shadow-tremor-dropdown border border-tremor-border">
            <p className="font-medium text-tremor-content-emphasis mb-1">{label}</p>
            <div className="flex flex-row">
                {payload.map((category, idx) => (
                    <div key={idx} className="flex flex-1 space-x-2.5 mt-1">
                        <div className={`w-1 flex flex-col bg-${category.color}-500 rounded`} />
                        <div className="space-y-1">
                            <p className="text-tremor-content">{category.dataKey}</p>
                            <p className="font-medium text-tremor-content-emphasis">{category.value}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const valueFormatter = function (number) {
    return number.toString();
};

export default function AuthDashboard({ usersInfo, authChart, totalAuthRequests }) {
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Debounce search term with 300ms delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Memoize expensive operations
    const sortedUsers = useMemo(() => {
        // convert usersInfo object to array and sort by creationTime
        return Object.values(usersInfo).sort((a, b) => {
            const dateA = new Date(a.creationTime);
            const dateB = new Date(b.creationTime);
            return dateB - dateA; // sort in descending order
        });
    }, [usersInfo]);

    // Memoize filtered users to only recalculate when debounced search term changes
    const filteredUsers = useMemo(() => {
        return sortedUsers.filter(user => {
            const email = user.email || '';
            return email.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
        });
    }, [sortedUsers, debouncedSearchTerm]);

    // Memoize pagination calculations
    const paginationData = useMemo(() => {
        const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

        return { totalPages, currentUsers };
    }, [filteredUsers, currentPage, itemsPerPage]);

    // Reset to first page when debounced search term or items per page changes
    useEffect(() => {
        setCurrentPage(1);
    }, [itemsPerPage, debouncedSearchTerm]);

    // Memoize today's users calculation
    const newUsersToday = useMemo(() => {
        const today = new Date();
        const todayUsers = sortedUsers.filter((user) => {
            const userCreationDate = new Date(user.creationTime);
            return userCreationDate.getDate() === today.getDate() &&
                userCreationDate.getMonth() === today.getMonth() &&
                userCreationDate.getFullYear() === today.getFullYear();
        });
        return todayUsers.length;
    }, [sortedUsers]);

    // calculate number of users
    const totalUsers = Object.keys(usersInfo).length;

    return (
        <div className="mt-4 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    {/* Users Overview Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Users Overview</h3>
                            <div className="flex items-center space-x-6">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-indigo-600">{newUsersToday}</div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wide">New Today</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600">{totalUsers}</div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wide">Total Users</div>
                                </div>
                            </div>
                        </div>
                        <div className="h-64">
                            <UsersChart chartData={authChart} />
                        </div>
                    </div>

                    {/* Modern Pagination Bar */}
                    <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">Users</h3>

                    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            {/* Left side - Search */}
                            <div className="flex items-center space-x-3">
                                <label htmlFor="searchEmail" className="text-sm font-medium text-gray-700">
                                    Search:
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <input
                                        id="searchEmail"
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Enter email..."
                                        className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                    />
                                </div>
                            </div>

                            {/* Center - Page Info */}
                            <div className="flex items-center justify-center lg:flex-1">
                                <div className="bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700 font-medium">
                                    Page {currentPage} of {paginationData.totalPages} • {filteredUsers.length} of {sortedUsers.length} items
                                </div>
                            </div>

                            {/* Right side - Controls */}
                            <div className="flex items-center space-x-4">
                                {/* Items per page */}
                                <div className="flex items-center space-x-2">
                                    <label htmlFor="itemsPerPage" className="text-sm font-medium text-gray-700">
                                        Show:
                                    </label>
                                    <select
                                        id="itemsPerPage"
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="block w-20 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>

                                {/* Navigation buttons */}
                                <div className="flex items-center space-x-1">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    >
                                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalPages))}
                                        disabled={currentPage === paginationData.totalPages}
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    >
                                        Next
                                        <svg className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                        Email
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        User Id
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Provider
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Email verified?
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Created At
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Last Login
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {paginationData.currentUsers.map((userBilling, index) => (
                                    <tr key={index}>
                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                            {userBilling.email}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            {userBilling.userId}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            {userBilling.provider}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            {userBilling.confirmed ? 'Yes' : (
                                                <span className='text-red-500'>No</span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            {userBilling.creationTime}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            {userBilling.lastSignInTime}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

const UsersChart = ({ chartData }) => {
    return (
        <AreaChart
            className="h-60 mt-4"
            data={chartData}
            index="day"
            categories={["Users"]}
            colors={["indigo", "cyan", "red"]}
            showAnimation={true}
            showLegend={true}
            allowDecimals={false}
            valueFormatter={valueFormatter}
            customTooltip={customTooltipUsers}
            yAxisWidth={68}
        />
    );
}